<template>
  <div class="temp-charts-modal">
    <!-- Title Bar -->
    <div class="title-bar">
      <div class="title-content">
        <button
          class="nav-arrow-btn"
          @click="$emit('navigate-prev')"
          title="Previous Trend"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        <h3>Temperature Charts</h3>
        <button
          class="nav-arrow-btn"
          @click="$emit('navigate-next')"
          title="Next Trend"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
        <select
          v-model="selectedDeviceId"
          @change="onDeviceChange"
          class="device-selector"
        >
          <option v-for="dev in devices" :key="dev.id" :value="String(dev.id)">
            {{ dev.device_name || dev.device_id }}
          </option>
        </select>
        <select
          v-model="selectedRange"
          @change="loadData"
          class="range-selector"
        >
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="all">All Data</option>
        </select>
        <select
          v-model="selectedProbe"
          @change="renderChart"
          class="range-selector"
        >
          <option value="both">Both Sensors</option>
          <option value="0">{{ sensorOptionLabel(1) }}</option>
          <option value="1">{{ sensorOptionLabel(2) }}</option>
        </select>
        <label class="unit-toggle" title="Toggle °F / °C">
          <input type="checkbox" v-model="useCelsius" @change="renderChart" />
          <span>°C</span>
        </label>
        <label
          class="auto-update-toggle"
          title="Auto-refresh at device sample interval"
        >
          <input
            type="checkbox"
            v-model="autoUpdate"
            @change="toggleAutoUpdate"
          />
          <span>Auto</span>
        </label>
        <span
          v-if="!loading && !error && summary.length"
          class="readings-count"
        >Readings: {{ totalReadings.toLocaleString() }}</span>
      </div>
      <div class="title-buttons">
        <button class="refresh-btn" @click="openSettings" title="Settings">
          <i class="fas fa-cog"></i>
        </button>
        <button class="refresh-btn" @click="printChart" title="Print Chart">
          <i class="fas fa-print"></i>
        </button>
        <button class="refresh-btn" @click="refreshAll" title="Refresh Data">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button class="close-btn" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <DeviceSettingsDialog
      v-if="showSettings && selectedDeviceId != null"
      :key="selectedDeviceId"
      :device-id="selectedDeviceId"
      @close="closeSettings"
      @saved="applySavedDeviceSettings"
      @erased="refreshAll"
    />

    <!-- Chart Container -->
    <div class="chart-body">
      <div class="chart-main">
        <div v-if="loading" class="loading-indicator">
          <i class="fas fa-spinner fa-spin"></i> Loading temperature data...
        </div>
        <div v-if="error" class="error-message">
          <i class="fas fa-exclamation-triangle"></i> {{ error }}
        </div>
        <div ref="chartRef" class="chart-container"></div>

        <!-- Summary row -->
        <div v-if="!loading && !error && summary.length" class="summary-row">
          <div class="summary-interval">({{ currentIntervalSec }} sec)</div>
          <div v-for="s in summary" :key="s.probe_id" class="summary-item">
            <span class="summary-label">{{ s.name }}:</span>
            <span class="summary-value">
              {{ formatTemp(s.min) }} / {{ formatTemp(s.avg) }} /
              {{ formatTemp(s.max) }}
            </span>
            <span class="summary-hint">(min / avg / max)</span>
          </div>
          <div class="summary-updated" v-if="lastUpdated">
            Updated: {{ lastUpdated }}
          </div>
        </div>
      </div>
      <aside class="device-rail" aria-label="Devices">
        <button
          v-for="dev in devices"
          :key="dev.id"
          type="button"
          class="device-rail-item"
          :class="{ active: String(dev.id) === String(selectedDeviceId) }"
          @click="selectDevice(dev.id)"
        >
          <span
            class="device-dot"
            :class="dev.connected ? 'is-connected' : 'is-offline'"
            :title="dev.connected ? 'Connected' : 'Not connected'"
          ></span>
          <span class="device-rail-text">
            <span
              class="device-rail-name"
              :title="dev.device_name || dev.device_id"
            >
              {{ dev.device_name || dev.device_id }}
            </span>
            <span class="device-rail-seen">{{
              formatLastSeen(dev.last_seen)
            }}</span>
          </span>
        </button>
        <div v-if="!devices.length" class="device-rail-empty">No devices</div>
      </aside>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import axios from '@/axiosConfig';
import Plotly from 'plotly.js-dist';
import DeviceSettingsDialog from '@/components/DeviceSettingsDialog.vue';
import {
  formatLastSeen,
  chartYRange,
  sensorDisplayName,
  chartSeriesName,
  readingMatchesProbeChoice,
} from '@/utils/tempChartDevices';

export default {
  name: 'TempCharts',
  components: {
    DeviceSettingsDialog,
  },
  props: {
    userRole: {
      type: Number,
      default: null,
    },
  },
  emits: ['close', 'navigate-prev', 'navigate-next'],
  setup() {
    const chartRef = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const devices = ref([]);
    const selectedDeviceId = ref(null);
    const selectedRange = ref('7d');
    const selectedProbe = ref('both');
    const useCelsius = ref(false);
    const summary = ref([]);
    const totalReadings = ref(0);
    const lastUpdated = ref('');
    const showSettings = ref(false);
    const autoUpdate = ref(true);
    let autoUpdateTimer = null;
    let statusTimer = null;

    function selectedDevice() {
      return devices.value.find(
        (d) => String(d.id) === String(selectedDeviceId.value),
      );
    }

    const currentIntervalSec = computed(() => {
      const device = selectedDevice();
      return device ? device.sample_interval_sec || 60 : 60;
    });

    // Store raw readings for re-render on unit toggle
    let rawReadings = [];

    const probeColorList = [
      { line: '#d32f2f', marker: '#ef5350' },
      { line: '#1565c0', marker: '#42a5f5' },
      { line: '#2e7d32', marker: '#66bb6a' },
      { line: '#7b1fa2', marker: '#ba68c8' },
    ];

    const getSinceDate = () => {
      const now = new Date();
      switch (selectedRange.value) {
        case '24h':
          return new Date(now - 24 * 60 * 60 * 1000).toISOString();
        case '7d':
          return new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
        case '30d':
          return new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
        case '90d':
          return new Date(now - 90 * 24 * 60 * 60 * 1000).toISOString();
        case 'all':
          return null;
        default:
          return new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
      }
    };

    const formatTemp = (val) => {
      if (val == null) return '--';
      return useCelsius.value ? `${val.toFixed(1)}°C` : `${val.toFixed(1)}°F`;
    };

    const loadDevices = async () => {
      try {
        const resp = await axios.get('/api/sensors/devices');
        devices.value = resp.data || [];
        if (devices.value.length && !selectedDeviceId.value) {
          selectedDeviceId.value = String(devices.value[0].id);
        }
      } catch (err) {
        console.error('Error loading devices:', err);
      }
    };

    const loadData = async () => {
      const device = selectedDevice();
      if (!device) return;

      loading.value = true;
      error.value = null;
      summary.value = [];
      totalReadings.value = 0;

      try {
        const params = {
          pico_id: device.id,
          order: 'asc',
          limit: 50000,
        };

        const since = getSinceDate();
        if (since) params.since = since;

        const resp = await axios.get('/api/sensors/readings', { params });
        rawReadings = resp.data.readings || [];
        totalReadings.value = resp.data.count || 0;

        // Format last-updated timestamp
        const now = new Date();
        const mo = now.getMonth() + 1;
        const dy = now.getDate();
        const yr = String(now.getFullYear()).slice(-2);
        let hr = now.getHours();
        const ampm = hr >= 12 ? 'pm' : 'am';
        hr = hr % 12 || 12;
        const mn = String(now.getMinutes()).padStart(2, '0');
        lastUpdated.value = `${mo}/${dy}/${yr} @ ${hr}:${mn}${ampm}`;

        renderChart();
      } catch (err) {
        console.error('Error loading readings:', err);
        error.value = 'Failed to load temperature data. Please try again.';
      } finally {
        loading.value = false;
      }
    };

    const renderChart = () => {
      if (!chartRef.value) return;
      if (!rawReadings.length) {
        Plotly.purge(chartRef.value);
        if (!error.value)
          error.value = 'No readings found for this device and time range.';
        summary.value = [];
        return;
      }

      error.value = null;

      const deviceForNames = selectedDevice();
      // Group by probe_id. Sensor 1 and Sensor 2 follow the probe number.
      const probeMap = {};
      rawReadings.forEach((r) => {
        if (!readingMatchesProbeChoice(r, selectedProbe.value)) return;
        const pid = r.probe_id || String(r.probe || '?');
        if (!probeMap[pid]) probeMap[pid] = [];
        probeMap[pid].push(r);
      });

      const tempField = useCelsius.value ? 'temp_c' : 'temp_f';
      const unitLabel = useCelsius.value ? '°C' : '°F';

      const traces = [];
      const summaryArr = [];
      const visibleTemps = [];
      const sortedProbeIds = Object.keys(probeMap).sort();

      // Filter probes based on dropdown selection
      const visibleProbeIds = sortedProbeIds;
      if (!visibleProbeIds.length) {
        Plotly.purge(chartRef.value);
        summary.value = [];
        if (!error.value) {
          error.value = 'No readings found for this device and time range.';
        }
        return;
      }

      visibleProbeIds.forEach((pid) => {
        const idx = sortedProbeIds.indexOf(pid);
        const readings = probeMap[pid];
        // Convert UTC reading_time to local time for display
        const times = readings.map((r) => {
          const utc = r.reading_time.endsWith('Z')
            ? r.reading_time
            : r.reading_time + 'Z';
          return new Date(utc);
        });
        const temps = readings.map((r) => parseFloat(r[tempField]));
        visibleTemps.push(...temps);

        const validTemps = temps.filter((t) => !isNaN(t));
        const minT = Math.min(...validTemps);
        const maxT = Math.max(...validTemps);
        const avgT = validTemps.reduce((a, b) => a + b, 0) / validTemps.length;

        const seriesName = chartSeriesName(readings[0], deviceForNames);
        summaryArr.push({
          probe_id: pid,
          name: seriesName,
          min: minT,
          max: maxT,
          avg: avgT,
        });

        const colors = probeColorList[idx] || {
          line: '#616161',
          marker: '#9e9e9e',
        };
        // Use dual Y axes only when showing both probes
        const yaxisKey =
          visibleProbeIds.length > 1 && idx > 0 ? `y${idx + 1}` : 'y';

        traces.push({
          x: times,
          y: temps,
          type: 'scatter',
          mode: 'lines+markers',
          name: seriesName,
          yaxis: yaxisKey,
          line: { color: colors.line, width: 2 },
          marker: {
            color: colors.marker,
            size: 5,
            line: { color: colors.line, width: 1 },
          },
          hovertemplate: `<b>${seriesName}</b><br>%{x|%b %d %H:%M}<br>%{y:.1f}${unitLabel}<extra></extra>`,
        });
      });

      summary.value = summaryArr;
      const yRange = chartYRange(visibleTemps);

      const device = selectedDevice();
      const deviceLabel = device
        ? device.device_name || device.device_id
        : selectedDeviceId.value;

      // Build y-axis color from visible probes
      const firstVisibleIdx = sortedProbeIds.indexOf(visibleProbeIds[0]);
      const y1Color = (probeColorList[firstVisibleIdx] || { line: '#616161' })
        .line;

      const showDualAxis =
        visibleProbeIds.length > 1 && sortedProbeIds.length > 1;
      const secondVisibleIdx = showDualAxis
        ? sortedProbeIds.indexOf(visibleProbeIds[1])
        : -1;
      const y2Color = showDualAxis
        ? (probeColorList[secondVisibleIdx] || { line: '#616161' }).line
        : '#616161';

      const layout = {
        title: {
          text: `${deviceLabel} — Temperature (${unitLabel})`,
          font: { size: 16, color: '#333' },
        },
        xaxis: {
          title: '',
          type: 'date',
          tickfont: { size: 11 },
          gridcolor: '#eee',
        },
        yaxis: {
          title: `${chartSeriesName(probeMap[visibleProbeIds[0]][0], deviceForNames)} (${unitLabel})`,
          titlefont: { color: y1Color },
          tickfont: { size: 12, color: y1Color },
          automargin: true,
          gridcolor: '#eee',
          range: yRange,
          autorange: false,
        },
        yaxis2: showDualAxis
          ? {
              title: `${chartSeriesName(probeMap[sortedProbeIds[1]][0], deviceForNames)} (${unitLabel})`,
              titlefont: { color: y2Color },
              tickfont: { size: 12, color: y2Color },
              overlaying: 'y',
              side: 'right',
              automargin: true,
              gridcolor: 'transparent',
              range: yRange,
              autorange: false,
            }
          : { visible: false },
        legend: {
          orientation: 'h',
          y: -0.12,
          x: 0.5,
          xanchor: 'center',
          font: { size: 12 },
        },
        hoverlabel: {
          bgcolor: 'white',
          bordercolor: '#ccc',
          font: { size: 12 },
        },
        margin: { t: 50, b: 60, l: 60, r: 60 },
        plot_bgcolor: '#fafafa',
        paper_bgcolor: 'white',
        hovermode: 'x unified',
      };

      const config = {
        responsive: true,
        displayModeBar: true,
        displaylogo: false,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
      };

      Plotly.newPlot(chartRef.value, traces, layout, config);
    };

    onMounted(async () => {
      await nextTick();
      await loadDevices();
      if (selectedDeviceId.value) {
        await loadData();
        if (autoUpdate.value) {
          toggleAutoUpdate();
        }
      }
      statusTimer = setInterval(() => {
        loadDevices();
      }, 15000);
    });

    onBeforeUnmount(() => {
      if (chartRef.value) {
        Plotly.purge(chartRef.value);
      }
      if (autoUpdateTimer) {
        clearInterval(autoUpdateTimer);
        autoUpdateTimer = null;
      }
      if (statusTimer) {
        clearInterval(statusTimer);
        statusTimer = null;
      }
    });

    function printChart() {
      const modal = chartRef.value?.closest('.temp-charts-modal');
      if (!modal) return;
      modal.classList.add('printing');
      nextTick(() => {
        window.print();
        modal.classList.remove('printing');
      });
    }

    function toggleAutoUpdate() {
      if (autoUpdateTimer) {
        clearInterval(autoUpdateTimer);
        autoUpdateTimer = null;
      }
      if (autoUpdate.value) {
        const device = selectedDevice();
        const intervalSec = device ? device.sample_interval_sec || 60 : 60;
        autoUpdateTimer = setInterval(() => {
          refreshAll();
        }, intervalSec * 1000);
      }
    }

    function onDeviceChange() {
      loadData();
      toggleAutoUpdate();
    }

    function selectDevice(id) {
      const next = String(id);
      if (!next || next === String(selectedDeviceId.value)) return;
      selectedDeviceId.value = next;
      onDeviceChange();
    }

    async function refreshAll() {
      await loadDevices();
      await loadData();
    }

    function sensorOptionLabel(which) {
      const device = selectedDevice();
      const stored =
        device && (which === 2 ? device.sensor2_name : device.sensor1_name);
      return sensorDisplayName(stored, which);
    }

    function openSettings() {
      if (!selectedDevice()) return;
      showSettings.value = true;
    }

    function closeSettings() {
      showSettings.value = false;
    }

    function applySavedDeviceSettings(row) {
      const device = selectedDevice();
      if (!device || !row) return;
      device.device_name = row.device_name;
      device.sample_interval_sec = row.sample_interval_sec;
      device.sensor1_name = row.sensor1_name;
      device.sensor2_name = row.sensor2_name;
      renderChart();
    }

    return {
      chartRef,
      loading,
      error,
      devices,
      selectedDeviceId,
      selectedRange,
      selectedProbe,
      useCelsius,
      summary,
      totalReadings,
      lastUpdated,
      currentIntervalSec,
      loadData,
      formatTemp,
      formatLastSeen,
      selectDevice,
      onDeviceChange,
      refreshAll,
      printChart,
      renderChart,
      showSettings,
      sensorOptionLabel,
      openSettings,
      closeSettings,
      applySavedDeviceSettings,
      autoUpdate,
      toggleAutoUpdate,
    };
  },
};
</script>

<style scoped>
.temp-charts-modal {
  position: relative;
  width: 85vw;
  max-width: 1360px;
  height: 80vh;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #26c6da;
  color: #fff;
  padding: 8px 15px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  font-size: 1.4em;
  flex-shrink: 0;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-arrow-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.6);
  color: #fff;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  font-size: 14px;
  padding: 0;
}

.nav-arrow-btn:hover {
  background: rgba(255, 255, 255, 0.35);
  border-color: #fff;
}

.title-content h3 {
  margin: 0;
  color: #fff;
  font-size: 1em;
  font-weight: 600;
  width: 220px;
  text-align: center;
}

.device-selector,
.range-selector {
  padding: 4px 8px;
  font-size: 0.7em;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  color: #333;
  cursor: pointer;
}

.device-selector:hover,
.range-selector:hover {
  border-color: #1ba3b3;
}

.device-selector:focus,
.range-selector:focus {
  outline: none;
  border-color: #1ba3b3;
  box-shadow: 0 0 3px rgba(38, 198, 218, 0.5);
}

.unit-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #fff;
  font-size: 0.7em;
  cursor: pointer;
  user-select: none;
}

.unit-toggle input {
  cursor: pointer;
}

.auto-update-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #fff;
  font-size: 0.7em;
  cursor: pointer;
  user-select: none;
}

.auto-update-toggle input {
  cursor: pointer;
}

.readings-count {
  color: #fff;
  font-size: 0.7em;
  font-weight: 600;
  white-space: nowrap;
}

.title-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
}

.refresh-btn,
.close-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #1ba3b3;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.refresh-btn {
  margin-right: 30px;
}

.refresh-btn:hover,
.close-btn:hover {
  background: #158a97;
  color: #fff;
  border-color: #fff;
}

.refresh-btn i,
.close-btn i {
  font-size: 12px;
}

.chart-body {
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  min-height: 0;
}

.chart-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 10px;
}

.device-rail {
  width: 240px;
  flex: 0 0 240px;
  overflow-y: auto;
  overflow-x: hidden;
  border-left: 1px solid #e0e0e0;
  background: #fafafa;
  padding: 8px;
}

.device-rail-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 6px;
  padding: 8px;
  cursor: pointer;
  margin-bottom: 4px;
  color: inherit;
  font: inherit;
}

.device-rail-item:hover {
  background: #f0f0f0;
}

.device-rail-item.active {
  background: #e0f7fa;
  border-color: #26c6da;
}

.device-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 4px;
  flex: 0 0 10px;
}

.device-dot.is-connected {
  background: #2e7d32;
}

.device-dot.is-offline {
  background: #c62828;
}

.device-rail-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.device-rail-name {
  font-size: 0.92em;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-rail-seen {
  font-size: 0.75em;
  color: #777;
  margin-top: 2px;
}

.device-rail-empty {
  color: #888;
  font-size: 0.85em;
  padding: 8px;
}

.chart-container {
  flex: 1;
  min-height: 0;
  width: 100%;
}

.loading-indicator {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1.1em;
}

.error-message {
  background: #ffebee;
  color: #c62828;
  padding: 10px 20px;
  border: 1px solid #ef9a9a;
  border-radius: 4px;
  margin: 10px;
}

.summary-row {
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 8px 20px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
  position: relative;
}

.summary-updated {
  position: absolute;
  right: 16px;
  font-size: 0.8em;
  color: #999;
}

.summary-interval {
  position: absolute;
  left: 16px;
  font-size: 0.8em;
  color: #999;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9em;
}

.summary-label {
  color: #666;
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
  color: #333;
}

.summary-hint {
  color: #999;
  font-size: 0.85em;
}
</style>

<style>
@media print {
  @page {
    size: landscape;
    margin: 0.5cm;
  }
  body * {
    visibility: hidden !important;
  }
  .temp-charts-modal.printing,
  .temp-charts-modal.printing * {
    visibility: visible !important;
  }
  .temp-charts-modal.printing {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    max-width: none !important;
    height: 100vh !important;
    border: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    z-index: 999999 !important;
  }
  .temp-charts-modal.printing .title-buttons,
  .temp-charts-modal.printing .device-rail {
    display: none !important;
  }
  .temp-charts-modal.printing .chart-container {
    flex: 1 !important;
    height: auto !important;
  }
}
</style>
